/**
 * The help centre's corpus (#120) — every article, as data.
 *
 * It lives under `convex/lib/` rather than as markdown files for one reason,
 * and it is worth stating because the location otherwise looks arbitrary:
 * **Convex functions cannot read the filesystem.** The Assistant is meant to
 * search these articles (the `search_docs` half of #118), so they have to be
 * importable by the backend. The web routes then read the same modules through
 * `~convex/lib/help`, exactly as `/admin/assistant` reads the tool specs — one
 * source, two consumers, and the user's help centre IS the AI's knowledge base
 * rather than a second description of the same product that drifts.
 *
 * Bodies are a deliberately small markdown subset, rendered by
 * `web/src/components/help/article-body.tsx`. Small because there is no
 * markdown pipeline in this repo and adding one to render help pages is not a
 * trade worth making; a subset the renderer fully covers cannot produce the
 * half-parsed output a general parser does on input it does not expect. A
 * model reads the same string natively, which is the other half of the point.
 *
 * ACCURACY IS THE WHOLE VALUE. An article describing a feature we do not have
 * is worse than a missing article: the reader acts on it, and the Assistant
 * repeats it. Every claim here is checked against `convex/schema.ts` and the
 * routes under `web/src/routes/`. When a feature changes, the article changes
 * in the same commit.
 *
 * pt-BR only, deliberately. That is where the users are and the only language
 * the Assistant speaks; `locale` is not modelled at all yet, so adding English
 * later is a schema change here rather than a translation pass.
 */

export type HelpCategory =
  | 'assistente'
  | 'biblioteca'
  | 'comecar'
  | 'configuracoes'
  | 'funil'
  | 'trabalho'

export type HelpArticle = {
  /**
   * The URL under `/ajuda`. Stable once published — it is what the app's Help
   * menu, the marketing site and any link a user saved all point at.
   */
  slug: string
  title: string
  /** One sentence. The hub card reads it, and so does the page description. */
  summary: string
  category: HelpCategory
  /** Markdown subset: `##`, paragraphs, `-`, `1.`, `**b**`, `` `c` ``, links. */
  body: string
}

/**
 * Category order and labels. Mirrors the app's own sidebar grouping (Funil /
 * Trabalho / Biblioteca) on purpose — someone looking for help has the nav in
 * front of them, and a help centre organised differently from the product is
 * one more thing to translate in their head.
 */
export const HELP_CATEGORIES: { key: HelpCategory; label: string; blurb: string }[] = [
  {
    blurb: 'O que é o Planno e como dar os primeiros passos.',
    key: 'comecar',
    label: 'Começar',
  },
  {
    blurb: 'Clientes, propostas e tudo o que o cliente enxerga do lado de fora.',
    key: 'funil',
    label: 'Funil e clientes',
  },
  {
    blurb: 'Projetos, tarefas, visitas de obra e ciclos de trabalho.',
    key: 'trabalho',
    label: 'Trabalho',
  },
  {
    blurb: 'Arquivos, produtos e os modelos que o escritório reaproveita.',
    key: 'biblioteca',
    label: 'Biblioteca',
  },
  {
    blurb: 'As listas, os dados e as pessoas do escritório — o que se configura uma vez.',
    key: 'configuracoes',
    label: 'Configurações',
  },
  {
    blurb: 'O Planno no WhatsApp: o que ele faz e como conectar.',
    key: 'assistente',
    label: 'O Assistente',
  },
]

const ARTICLES: HelpArticle[] = [
  {
    body: `O Planno é onde um escritório de arquitetura toca o trabalho inteiro: da primeira conversa com um cliente em potencial até a entrega da obra. A ideia é simples — parar de espalhar o escritório entre WhatsApp, planilha, Drive e caderno.

## O caminho de um trabalho

Todo trabalho no Planno segue o mesmo caminho, e é um registro só do começo ao fim:

1. Alguém procura o escritório. Você registra essa pessoa no **Funil**, e nasce um projeto na primeira etapa.
2. Você conversa, faz um briefing, manda uma proposta.
3. O cliente aceita. O projeto **cruza a linha do contrato** e passa a aparecer em Projetos, agora nas etapas de execução.
4. Daí em diante é obra: tarefas, visitas, arquivos, produtos e o portal do cliente.

O que muda entre o funil e a execução é a **etapa** em que o projeto está. O registro é o mesmo, com o mesmo histórico, do primeiro contato à entrega.

## Por onde começar

- Cadastre o escritório em **Configurações** — nome, logo e os dados que aparecem nas propostas.
- Revise as **etapas** do funil e da execução. Elas vêm com um padrão, mas são suas: renomeie, remova, acrescente.
- Traga um trabalho real. Um projeto de verdade ensina mais sobre o Planno do que qualquer projeto de teste.

## Convidar a equipe

Em **Configurações → Pessoas** você convida quem trabalha com você. Cada pessoa entra como administrador ou membro; administrador mexe nas configurações do escritório, membro trabalha normalmente em tudo o mais.`,
    category: 'comecar',
    slug: 'primeiros-passos',
    summary:
      'O que é o Planno, o caminho que um trabalho percorre e o que fazer nos primeiros dias.',
    title: 'Primeiros passos',
  },

  {
    body: `Esta é a parte do Planno que mais confunde no começo, e a resposta é mais simples do que parece: **funil e projeto são a mesma coisa.**

Não existem "leads" de um lado e "projetos" de outro. É um registro só. Ele nasce no funil, quando alguém procura o escritório, e passa para execução quando o contrato fecha. O que muda é a etapa.

## A linha do contrato

O Planno divide a vida de um projeto em dois lados:

- **Antes do contrato** — o projeto aparece no **Funil**, nas etapas de captação: novo, em contato, proposta enviada.
- **Depois do contrato** — o mesmo projeto aparece em **Projetos**, nas etapas de execução: levantamento, estudo preliminar, executivo, detalhamento.

Quando o cliente aceita a proposta, o projeto cruza essa linha. Ele não é recriado nem duplicado: sai de uma lista e entra na outra, levando junto tudo o que já tinha — anotações, arquivos, histórico.

## Cliente é outra coisa

O **cliente** é a pessoa ou a empresa. O **projeto** é o trabalho. Todo projeto pertence a um cliente, e o mesmo cliente pode ter vários projetos — a casa, depois o escritório, depois a reforma da casa.

Por isso registrar um cliente novo no funil cria duas coisas ao mesmo tempo: o cliente, se ele ainda não existia, e o primeiro projeto dele.

## Etapas são suas

As etapas dos dois lados vêm preenchidas, mas pertencem ao escritório. Em **Configurações → Etapas** você renomeia, reordena, remove e acrescenta. Se o seu processo chama "anteprojeto" o que o padrão chama "estudo preliminar", mude — o Planno passa a falar a sua língua, inclusive o Assistente no WhatsApp.

## Projeto perdido

Um projeto que não fechou é marcado como perdido e sai do funil, sem ser apagado. Ele continua no histórico do cliente, que é o que você quer quando a mesma pessoa volta dois anos depois.`,
    category: 'funil',
    slug: 'funil-e-projetos',
    summary:
      'Por que um lead e um projeto são o mesmo registro, o que é a linha do contrato e como as etapas funcionam.',
    title: 'Funil e projetos: um registro só',
  },

  {
    body: `O **cliente** é a pessoa ou a empresa para quem o escritório trabalha. Ele fica em **Clientes**, e é a base de tudo o mais: todo projeto pertence a um cliente.

## Pessoa ou empresa

São dois tipos de cadastro, e o Planno pede coisas diferentes de cada um:

- **Pessoa física** — nome completo e CPF.
- **Pessoa jurídica** — razão social, nome fantasia (quando existe) e CNPJ.

Os dois carregam e-mail, telefone e anotações. O Planno confere CPF, CNPJ, e-mail e telefone na hora de salvar, e avisa quando o documento já está em outro cadastro — que costuma ser o jeito de descobrir que a mesma pessoa foi cadastrada duas vezes.

## Um cliente, vários projetos

O cliente é a pessoa; o projeto é o trabalho. O mesmo cliente pode ter vários projetos ao longo dos anos — a casa, depois a sala comercial, depois a reforma da casa. Cada um é um projeto, com etapas e histórico próprios.

É por isso que cliente e projeto são registros separados. Se essa divisão ainda não estiver clara, vale ler [funil e projetos](/ajuda/funil-e-projetos).

## De onde vem um cliente novo

Três portas, todas chegando ao mesmo cadastro:

- Em **Clientes**, no botão de novo cliente. É assim que se registra alguém que ainda não tem projeto nenhum.
- No campo de cliente de um projeto, digitando um nome que ainda não existe.
- No **Funil**, ao registrar quem acabou de procurar o escritório: aí nascem os dois de uma vez, o cliente e o primeiro projeto dele.

## Apagar um cliente

Apagar um cliente leva junto os projetos dele, e o que estava dentro deles. Antes de confirmar, o Planno mostra a conta do que vai junto, então dá para desistir a tempo.

Duas coisas ficam: os arquivos continuam na biblioteca do escritório, e os briefings já respondidos são preservados. E você não desfaz isso sozinho — se apagou sem querer, fale com a gente pela bolha de suporte no canto da tela.

Para um cadastro duplicado, apague o que não tem projeto nenhum pendurado.`,
    category: 'funil',
    slug: 'clientes',
    summary:
      'Como cadastrar pessoa física ou jurídica, por que um cliente pode ter vários projetos e o que acontece ao apagar um cadastro.',
    title: 'Clientes',
  },

  {
    body: `As **propostas** do Planno são documentos que o cliente abre por um link, lê no celular e aceita — sem PDF anexado em e-mail e sem imprimir nada.

## Três tipos, um editor

O mesmo editor produz três coisas:

- **Proposta** — tem preço e o cliente aceita ou recusa.
- **Apresentação** — nem preço nem aceite. Serve para mostrar o escritório ou uma ideia.
- **Contrato** — preço, aceite e as cláusulas.

## O caminho de uma proposta

1. **Rascunho** — você monta o documento. Só o escritório enxerga.
2. **Enviada** — o Planno gera um link. É isso que você manda para o cliente.
3. **Aceita** ou **recusada** — o cliente decide na própria página. Você vê a resposta no projeto.

Uma proposta enviada também pode ser **revogada**, quando o link não deve mais valer.

Enquanto o documento é uma proposta em aberto, ele acompanha o que mudou no projeto. No momento em que o cliente aceita, o conteúdo **congela**: o que ele aceitou é o que fica registrado, e mudanças posteriores no escritório não reescrevem o que foi combinado.

## O que o cliente vê

Uma página só dele, com o nome do escritório, o conteúdo do documento e — quando é proposta ou contrato — os botões de aceitar e recusar. Ele não precisa de conta, nem de senha, nem de aplicativo.

## Portal do cliente

Separado das propostas existe o [portal do cliente](/ajuda/portal-do-cliente): uma página onde ele acompanha o andamento do projeto depois que o contrato fecha. É o lugar de mostrar a evolução da obra sem virar mais um grupo de WhatsApp.`,
    category: 'funil',
    slug: 'propostas',
    summary:
      'Propostas, apresentações e contratos: como o cliente recebe o link, aceita, e o que congela no aceite.',
    title: 'Propostas, apresentações e contratos',
  },

  {
    body: `O **portal** é uma página pública do projeto, só dele, onde o cliente acompanha o trabalho. Ele abre por um link, no celular, sem conta e sem senha.

## Nada aparece sem você publicar

Esse é o princípio inteiro do portal, e vale entender antes de ligar: ele nasce vazio. Os arquivos do projeto, as tarefas, as anotações e os valores **não** vão para lá sozinhos. O cliente vê o que você publicou, item por item, e mais nada.

## Ligar o portal

Na aba **Portal** do projeto, ative. O Planno gera o link e você manda para o cliente.

Só projeto com contrato fechado tem portal. Quem ainda está no funil não tem — o que o cliente recebe nessa fase é a [proposta](/ajuda/propostas).

## Publicações

Cada bloco do portal é uma publicação, de um dos três tipos:

- **Galeria** — fotos em grade, que o cliente abre uma a uma. É como se mostra o andamento da obra.
- **Documentos** — uma lista de arquivos para baixar: contrato, memorial, plantas.
- **Produtos** — a lista de compras do projeto, com ambientes, quantidades e o total estimado. No máximo uma por projeto, e ela é montada ao vivo: o que você mudar em [Produtos](/ajuda/produtos), o cliente vê.

Publicar é imediato — não existe rascunho. Para tirar algo do ar sem perder o trabalho, **esconda** a publicação: ela some do portal e continua na sua lista.

Apagar uma publicação não apaga o conteúdo: os arquivos continuam no projeto e os produtos, no acervo. Some só a vitrine.

## Andamento

Além das publicações, o portal pode mostrar em que etapa de execução o projeto está. É um interruptor: se você prefere não expor o ritmo interno, desligue.

## O link

- **Copiar** e mandar para o cliente.
- **Gerar um novo link** derruba o anterior na hora. Serve para quando o link foi parar onde não devia.
- **Desativar** tira o portal do ar e o cliente perde o acesso. As publicações ficam guardadas; reativar cria um link novo e tudo reaparece.

Gerar novo link e desativar são ações de administrador — criar e publicar, não.

Um arquivo publicado que depois vai para a lixeira simplesmente deixa de aparecer para o cliente, e a sua lista de publicações avisa que ele está faltando.`,
    category: 'funil',
    slug: 'portal-do-cliente',
    summary:
      'A página em que o cliente acompanha o projeto: como ligar, o que é uma publicação e por que nada aparece sozinho.',
    title: 'Portal do cliente',
  },

  {
    body: `As **tarefas** são a lista do que precisa ser feito. Elas podem viver soltas no escritório ou penduradas em um projeto.

## O que uma tarefa carrega

- **Título** — o que precisa ser feito, em uma linha.
- **Descrição** — texto livre, para o detalhe que não cabe no título.
- **Responsável** — alguém da equipe, ou ninguém.
- **Datas** — quando começa e quando entrega. Pode ter as duas, só uma, ou nenhuma.
- **Prioridade** — de nenhuma a urgente.
- **Projeto** — a que trabalho ela pertence, quando pertence a algum.
- **Etiquetas** — os seus próprios rótulos, para cortar a lista do seu jeito.
- **Status** — a coluna em que ela está.

## Status são do escritório

Assim como as etapas, os status das tarefas pertencem a você. Em **Configurações → Status** dá para renomear, reordenar e criar os seus. Cada status pertence a uma categoria — a fazer, em andamento, concluída, cancelada — e é isso que o Planno usa para saber o que ainda está em aberto.

## Subtarefas

Uma tarefa pode ter subtarefas, para quebrar um item grande sem poluir a lista principal.

## Onde as tarefas aparecem

- Em **Tarefas**, a lista completa do escritório, com filtros que você pode salvar.
- Dentro de cada **projeto**, só as daquele trabalho.
- No [Calendário](/ajuda/calendario), as que têm data.
- No **WhatsApp**, se você usa o Assistente: "cria uma tarefa pra mandar o memorial até sexta" vira uma tarefa de verdade.`,
    category: 'trabalho',
    slug: 'tarefas',
    summary:
      'O que uma tarefa carrega, como os status do escritório funcionam e onde as tarefas aparecem.',
    title: 'Tarefas',
  },

  {
    body: `O **Calendário** é o quadro de tarefas visto pelo tempo. Ele responde uma pergunta só: o que cai nesta semana.

## O que aparece

Tarefas com data. Uma tarefa pode ter data de **início** e data de **entrega**: com as duas, ela vira uma barra que atravessa os dias; com só uma, aparece naquele dia.

Tarefa sem data nenhuma não aparece aqui — ela está em **Tarefas**, esperando ser agendada. E vale dizer o que o calendário não é: ele mostra tarefas. Visitas de obra e propostas não entram nele.

## Mês, semana e celular

Alterne entre **mês** e **semana** no topo da página. No celular, o mês vira uma agenda em lista, que é o formato que se lê com o polegar.

## Arrastar para remarcar

Arraste uma tarefa para outro dia e a data muda ali mesmo. É a forma mais rápida de replanejar a semana: você vê o acúmulo e desloca o que não cabe, sem abrir tarefa por tarefa.

## Filtrar

Os mesmos filtros do quadro de tarefas: projeto, responsável, etiqueta, prioridade, status e sprint. Filtre por responsável para ver a semana de uma pessoa; por projeto, para ver a de uma obra.

Clicar num dia vazio cria uma tarefa já com a entrega naquela data.`,
    category: 'trabalho',
    slug: 'calendario',
    summary:
      'As tarefas do escritório vistas pelo tempo: o que aparece, como remarcar arrastando e o que o calendário não mostra.',
    title: 'Calendário',
  },

  {
    body: `A **visita** é a ida à obra registrada no Planno: com data, responsável e o que foi encontrado lá.

## O que cabe dentro de uma visita

- **Checklist** — os pontos a conferir, a partir de um modelo do escritório.
- **Fotos** — o registro do que você viu.
- **Pendências** — o que ficou por resolver.

## Pendências

A pendência é a peça que faz a visita valer depois que você sai da obra. Cada uma tem descrição, local em palavras ("banheiro da suíte", "fachada oeste") e as fotos que mostram o problema.

Uma pendência pode:

- ser **resolvida**, quando o problema foi corrigido;
- **virar tarefa**, quando alguém precisa fazer algo a respeito — e continua ligada à visita que a levantou.

## O que ficou aberto de outras visitas

Na página de uma visita, abaixo das pendências dela, aparecem as que continuam abertas em **outras visitas do mesmo projeto**. É a primeira coisa que se quer saber ao chegar na obra: o que eu apontei da última vez já foi resolvido?

Elas podem ser resolvidas ali mesmo, de dentro da visita em que você está. Quando isso acontece, o Planno registra em qual visita a correção foi confirmada — então o relatório da visita que levantou o problema mostra não só "resolvida", mas onde e quando alguém conferiu.`,
    category: 'trabalho',
    slug: 'visitas',
    summary:
      'Registrar a ida à obra: checklist, fotos, pendências, e o que continua aberto de visitas anteriores.',
    title: 'Visitas de obra e pendências',
  },

  {
    body: `O **Assistente** é o Planno dentro do WhatsApp. Você manda áudio, foto ou texto do jeito que já manda para qualquer pessoa, e ele organiza no escritório.

## Como conectar

1. No Planno, abra **Conta → WhatsApp**.
2. Gere o código de conexão.
3. Toque no link, que abre o WhatsApp já com a mensagem pronta, e envie.

O número que enviar a mensagem passa a ser o seu. Cada pessoa da equipe conecta o próprio.

## Como falar com ele

Do jeito que for mais fácil:

- um **áudio** contando como foi a visita;
- a **foto** da obra ou do documento que chegou;
- a **mensagem do cliente** encaminhada;
- ou só escrever o que não pode esquecer.

Ele espera você terminar de falar. Se você mandar quatro mensagens seguidas, ele responde as quatro juntas, uma vez só — não uma resposta por mensagem.

## O que ele faz

Cria e altera tarefas — inclusive a descrição, que ele acrescenta ao que já estava escrito, ou reescreve se você pedir. Anota decisões no projeto, move o projeto de etapa, registra cliente novo no funil, arquiva foto e documento na pasta do projeto certo, e responde sobre agenda, tarefas em aberto, situação de projeto e contato de cliente. Também procura no histórico da conversa: "o que eu falei sobre a bancada?".

Mandou o **link de um produto**? Ele guarda no [acervo](/ajuda/produtos) e o Planno preenche nome, marca, preço e foto sozinho. Diga o projeto na mesma mensagem e ele já entra na lista daquela obra.

No fim de cada resposta ele lista o que fez. Se ele fez algo errado, "desfaz isso" desfaz — dentro da última hora.

## O que ele não faz

Ele só afirma o que realmente fez. Quando falta ferramenta para o que você pediu, ele diz, e diz onde a coisa fica no aplicativo. Ele não inventa projeto, cliente, prazo ou valor que não esteja no escritório.

Instruções que aparecem **dentro** de uma mensagem encaminhada não são ordens para ele: são conteúdo do cliente, e ele confirma com você antes de agir.`,
    category: 'assistente',
    slug: 'assistente-whatsapp',
    summary: 'Conectar o WhatsApp, como falar com o Assistente, o que ele faz e o que ele não faz.',
    title: 'O Assistente no WhatsApp',
  },

  {
    body: `Chamamos de **biblioteca** a parte do Planno que não pertence a um trabalho só: o que o escritório acumula e reaproveita de projeto em projeto.

São três coisas, cada uma com a sua página:

- **[Arquivos](/ajuda/arquivos)** — as pastas do escritório e as de cada projeto. Onde ficam plantas, fotos e documentos.
- **[Produtos](/ajuda/produtos)** — o acervo de acabamentos e materiais que vocês já especificaram, com marca, preço e link da loja.
- **[Modelos](/ajuda/modelos-e-briefings)** — os formulários que se repetem: briefings que o cliente responde e checklists de visita de obra.

## Por que as três estão juntas

Um escritório repete muita coisa. O mesmo porcelanato, as mesmas perguntas de briefing, os mesmos pontos de vistoria. Refazer isso a cada projeto é trabalho jogado fora, e — pior — é informação que some quando alguém sai da equipe.

A biblioteca é onde essa repetição vira vantagem: você especifica uma vez, reaproveita nos próximos, e continua conseguindo procurar depois. Um projeto termina; a biblioteca fica.`,
    category: 'biblioteca',
    slug: 'biblioteca',
    summary:
      'O que se acumula e se reaproveita de um trabalho para o outro: arquivos, produtos e modelos.',
    title: 'A biblioteca',
  },

  {
    body: `Todo arquivo do escritório vive em **Arquivos**, organizado em pastas — como qualquer drive, e de propósito: ninguém precisa aprender um sistema novo para guardar uma planta.

## Duas bibliotecas

- **Arquivos**, no menu, é a biblioteca do escritório: o que não pertence a um trabalho específico. Modelos de contrato, material de apresentação, referências.
- Cada projeto tem a **sua** aba de arquivos, com as próprias pastas. É onde ficam plantas, fotos e documentos daquela obra.

Um arquivo mora em um dos dois lugares — ele não aparece nos dois ao mesmo tempo.

## Subir arquivos

Arraste para a janela ou use o botão de upload. Vários de uma vez funcionam; **pastas inteiras ainda não** — arraste os arquivos, não a pasta que os contém.

Se o nome já existir ali, o Planno pergunta o que fazer: **manter os dois**, e o novo entra com outro nome, ou **substituir**. Enquanto sobem, os arquivos aparecem numa bandeja no canto, e é de lá que você repete o que falhou.

## Achar

A lista tem busca por nome, ordenação (nome, mais recente, mais antigo, maior, tipo) e dois modos de ver: lista ou grade. Grade é melhor para foto; lista, para documento.

## Lixeira

Apagar um arquivo ou uma pasta manda para a **Lixeira** — nada some na hora. Fica lá **30 dias** e depois o Planno remove em definitivo. Apagar uma pasta manda junto tudo o que estava dentro.

Da Lixeira dá para restaurar. Se a pasta de origem não existir mais, o Planno avisa e devolve o arquivo assim mesmo, em vez de recusar. Também dá para esvaziar a lixeira na hora — e aí não tem volta.

## Fotos que chegam pelo WhatsApp

O que você manda para o [Assistente](/ajuda/assistente-whatsapp) **não** entra direto na biblioteca. A foto fica junto da conversa até você dizer onde guardar ("arquiva essa foto no projeto Bertoldi"); só então ela vira arquivo do projeto.

É uma escolha, não um esquecimento: a biblioteca é o acervo do escritório, e um print mandado para tirar uma dúvida não é um documento do escritório.`,
    category: 'biblioteca',
    slug: 'arquivos',
    summary:
      'Pastas do escritório e de cada projeto, upload, conflito de nome, e a lixeira de 30 dias.',
    title: 'Arquivos',
  },

  {
    body: `**Produtos** é o acervo do escritório: todo acabamento, louça, metal, luminária e móvel que vocês já especificaram, num lugar só. É o que responde "qual era o porcelanato da Bertoldi?" três anos depois, sem ninguém precisar abrir a planilha antiga.

## Duas formas de cadastrar

- **Colar o link da loja.** O Planno abre a página e preenche sozinho nome, marca, preço e imagem. Leva alguns segundos, e a linha avisa enquanto está capturando. Se a loja não deixar ler, ela marca que falhou — colar o link de novo é a forma de tentar outra vez.
- **Na mão**, quando não há link: nome, marca, preço, link e anotações.

## O preço é uma foto, não um acompanhamento

O preço fica guardado com a data em que foi capturado, e não se atualiza sozinho depois. O acervo é uma **especificação**, não um comparador de preços: ele registra por quanto aquilo estava quando vocês especificaram, que é o número de que você precisa ao revisar um orçamento antigo.

## Levar um produto para um projeto

Na aba **Produtos** do projeto você puxa itens do acervo e monta a lista de compras. Cada linha tem quantidade e **ambiente** ("Sala de estar", "Banheiro da suíte"), que é texto livre — você nomeia os ambientes como quiser.

O mesmo produto pode entrar duas vezes no mesmo projeto, em ambientes diferentes. Isso é o caso normal, não um erro.

O acervo mostra em quantos projetos cada produto já foi usado, e é assim que você descobre o que virou padrão da casa.

## Mostrar a lista para o cliente

A lista de compras do projeto pode ir para o [portal do cliente](/ajuda/portal-do-cliente) como uma publicação, com o total estimado. Ela é montada ao vivo: mudou aqui, mudou lá.

## Apagar

Apagar um produto tira ele do acervo **e** das listas de todos os projetos em que estava, portal incluído. Por isso o aviso de confirmação mostra em quantos projetos ele está antes de você decidir.`,
    category: 'biblioteca',
    slug: 'produtos',
    summary:
      'O acervo de acabamentos e materiais: capturar por link da loja, preço congelado e a lista de compras de cada projeto.',
    title: 'Produtos',
  },

  {
    body: `**Modelos** são os formulários que o escritório reaproveita de um trabalho para o outro. São de dois tipos, e eles vivem em momentos diferentes:

- **Briefing** — as perguntas do começo, respondidas pelo cliente.
- **Checklist** — os pontos a conferir numa [visita de obra](/ajuda/visitas), preenchidos por alguém da equipe.

## Os modelos do Planno e os seus

A aba **Planno** traz modelos prontos, que você pode ler mas não editar. Para mexer, **duplique** para o escritório: a cópia é sua, e a partir dali ela segue o seu caminho, não o nosso.

Os briefings são organizados por tipo de projeto — residencial, comercial, especializado. Os checklists, por fase da obra — fundação, estrutura, alvenaria, acabamento, entrega — e, quando faz sentido, por disciplina: elétrica, hidráulica, estrutural.

## As perguntas

Um modelo é feito de perguntas de vários tipos: texto curto, texto longo, número, data, escolha, nota, caixa de marcar, foto, várias fotos e escolher um cliente da lista.

Os checklists têm um tipo a mais, e ele é o motivo de existirem separados: **conformidade**, com três respostas — Conforme, Não conforme e N/A. Uma caixa de marcar não serve para vistoria, porque "não marcado" mistura "está errado" com "não se aplica", e essas duas levam a lugares opostos. É o **Não conforme** que puxa a criação de uma pendência.

## Enviar um briefing

Na visão geral do projeto você escolhe um modelo de briefing e gera um **link**. Mande para o cliente: ele responde pelo navegador, sem conta e sem senha, e a resposta cai no projeto. Um link pode ser revogado quando não deve mais valer.

O checklist não tem link — quem preenche é a equipe, de dentro da visita.

## Versões: por que uma resposta antiga não muda

Cada vez que você publica alterações num modelo, o Planno guarda aquela **versão**, e toda resposta fica presa à versão contra a qual foi respondida.

Isso importa mais do que parece. Se você reescrever o briefing hoje, o que o cliente respondeu no ano passado continua mostrando **exatamente as perguntas que ele viu**. Sem isso, respostas antigas apareceriam embaixo de perguntas que ninguém fez àquela pessoa — e ninguém teria como perceber.

Pelo mesmo motivo, um modelo que já tem respostas não pode ser apagado: apagar deixaria essas respostas sem pergunta nenhuma.`,
    category: 'biblioteca',
    slug: 'modelos-e-briefings',
    summary:
      'Briefings que o cliente responde e checklists de obra: modelos prontos, tipos de pergunta e por que as versões importam.',
    title: 'Modelos e briefings',
  },

  {
    body: `Os **sprints** são ciclos de trabalho de duração fixa — na maioria dos escritórios, uma semana. Em cada ciclo você escolhe as tarefas que entram, e no fim vê o que saiu.

Nem todo escritório usa, e o recurso vem **desligado**. Um administrador liga em **Configurações → Sprints**.

## Como configurar

- **Duração** do ciclo, em dias.
- **Dia da semana** em que começa.
- Quantos ciclos futuros ficam abertos para planejar.

## Como funciona

O Planno abre e fecha os ciclos sozinho, no dia configurado. Tarefas que você colocou no ciclo aparecem juntas; o que não foi concluído não some — continua na sua lista, e você decide se entra no próximo.

Quando um sprint fecha, o Planno guarda uma foto do que havia nele. Essa foto não muda depois, mesmo que as tarefas mudem — é o registro do que aquele ciclo realmente foi.`,
    category: 'trabalho',
    slug: 'sprints',
    summary:
      'Ciclos de trabalho de duração fixa: como ligar, configurar e o que acontece quando um fecha.',
    title: 'Sprints',
  },

  {
    body: `Duas coisas parecidas que fazem trabalhos diferentes. Confundir as duas é o tropeço mais comum de quem está configurando o escritório.

- A **etapa** diz em que ponto do processo o trabalho está: levantamento, estudo preliminar, executivo. Ela anda para a frente.
- O **status** é a coluna do quadro: em andamento, pausado, concluído. Ele diz como a coisa está, não em que ponto ela está.

Um projeto tem os dois ao mesmo tempo. Uma tarefa tem só status.

## Etapas

As etapas ficam em **Configurações → Etapas**, e só administrador mexe nelas. São duas listas:

- **Antes do contrato** — as colunas do funil. Uma lista só, para o escritório inteiro.
- **Depois do contrato** — as etapas de execução. Aqui existe uma lista padrão e, se você quiser, uma lista própria por tipo de projeto: uma reforma não passa pelas mesmas etapas que uma obra nova.

Cada etapa tem um nome e um tipo. O tipo é só uma dica visual para o ícone — ele não muda comportamento nenhum. Uma lista aceita até 20 etapas.

## Mexer nas etapas não mexe nos projetos que já existem

Quando um projeto nasce, ele leva uma **cópia** da lista daquele momento. Renomear, reordenar ou remover uma etapa depois muda o que os **próximos** projetos vão ver; os que já existem seguem com a lista deles.

Isso vale inclusive para remover: um projeto que está na etapa removida continua nela até você movê-lo. Nada é reatribuído sozinho — que é exatamente o que você quer quando o processo muda no meio do ano e há dez obras em andamento.

Se você se perder, dá para restaurar a lista padrão do Planno. E, de novo: os projetos existentes ficam como estão.

## Status

Os status ficam em **Configurações → Status**, também restrito a administrador, e são dois quadros separados:

- **Status de tarefa** — as colunas do quadro de tarefas.
- **Status de projeto** — as colunas do quadro de projetos.

Em cada um você renomeia, escolhe a cor, reordena as colunas e cria as suas.

## A categoria é o que o Planno realmente lê

Todo status pertence a uma **categoria**, escolhida quando ele é criado e fixa depois disso. É a categoria — não o nome — que o Planno usa para saber o que está aberto, o que está concluído e o que entra na conta de um [sprint](/ajuda/sprints).

Nas tarefas as categorias são: backlog, não iniciado, em andamento, concluído e cancelado. Nos projetos: ativo, pausado, concluído e arquivado.

Na prática isso quer dizer que você tem liberdade quase total: renomear "Concluído" para "Entregue", criar uma segunda coluna de revisão ou dar nomes internos às colunas não quebra nenhuma conta, porque as contas seguem a categoria.

O que não dá é trocar a categoria de um status depois de criado. Se errou, crie outro com a categoria certa e mova as tarefas para ele.`,
    category: 'configuracoes',
    slug: 'etapas-e-status',
    summary:
      'A diferença entre etapa e status, como personalizar as duas listas e por que mexer nelas não altera projetos antigos.',
    title: 'Etapas e status',
  },

  {
    body: `**Configurações** é onde o escritório se descreve. Boa parte disso não fica só para uso interno: vira o que o cliente lê nos documentos que você gera.

Quase tudo aqui é de administrador. Quem é membro enxerga, mas não edita.

## Geral

- **Identidade** — o nome do escritório.
- **Marca** — o logo e a cor de destaque, que aparecem no topo de propostas, briefings e do portal do cliente. Depois de subir o logo, o Planno sugere cores tiradas dele.
- **Registro** — os dados formais: a descrição que fica embaixo do nome ("arquitetos associados"), razão social, endereço, cidade, estado, CNPJ ou CPF, o registro no CAU ou CREA, e-mail e telefone de contato.

Nome e razão social são campos diferentes de propósito. O nome é como o escritório se chama e é o que assina as páginas; a razão social é o que precisa constar num contrato — "Bicudo & Farah" e "Bicudo & Farah Arquitetos Associados Ltda." não são a mesma linha.

Nada disso é obrigatório. O que ficar em branco simplesmente não é impresso, então você nunca terá um documento com um rótulo e nada depois dele.

## Perfil do escritório

Aqui mora o texto institucional, escrito uma vez e reaproveitado em todo documento que o cliente recebe:

- **Sobre** — quem é o escritório.
- **Fotos** — imagens do estúdio e do trabalho.
- **Depoimentos** — o que clientes já disseram.
- **Como trabalhamos** — as etapas do seu processo, explicadas para quem está contratando.

Vale gastar uma tarde nisso uma vez. É o conteúdo que faz uma proposta parecer o escritório e não um formulário.

## Pessoas

Convide quem trabalha com você. Cada pessoa entra como **administrador** ou **membro**: administrador mexe nas configurações do escritório e na equipe; membro trabalha normalmente em projetos, tarefas, arquivos e clientes.

## As outras abas

- **[Etapas e Status](/ajuda/etapas-e-status)** — as listas que o escritório usa no funil, na execução e nos quadros.
- **[Sprints](/ajuda/sprints)** — liga ou desliga os ciclos de trabalho.
- **Assinatura** — o plano do escritório e a cobrança.

## Mais de um escritório

Dá para tocar mais de um escritório na mesma conta, cada um com seus projetos, sua equipe e suas configurações. Não é um botão: fale com a gente pela bolha de suporte no canto da tela, e abrimos o segundo já configurado e com a cobrança certa.

Uma coisa não muda depois: o endereço do escritório no Planno. Renomear o escritório troca o nome que aparece nas telas, mas o link continua o mesmo — de propósito, para não quebrar o que já foi salvo ou compartilhado.`,
    category: 'configuracoes',
    slug: 'configuracoes-do-escritorio',
    summary:
      'Nome, marca, dados de registro, perfil institucional e equipe — o que se configura uma vez e aparece em tudo.',
    title: 'Configurações do escritório',
  },
]

/** Every article, in the order the hub lists them within each category. */
export const HELP_ARTICLES: readonly HelpArticle[] = ARTICLES

export const helpArticleBySlug = (slug: string): HelpArticle | undefined =>
  ARTICLES.find((article) => article.slug === slug)

export const helpArticlesIn = (category: HelpCategory): HelpArticle[] =>
  ARTICLES.filter((article) => article.category === category)
